/**
 * FocalPoint
 * The container that will maintain focus
 */

import React from 'react';

let timer: number | null = null;
const isDOM = typeof document !== 'undefined';

interface Props {
  element?: string;
  className?: string;
  children: React.ReactNode;
}

class FocalPoint extends React.Component<Props> {
  static defaultProps = {
    element: 'div',
  };

  anchor = isDOM ? document.activeElement : null;
  rootRef = React.createRef<HTMLElement>();

  contains(element: Element | null) {
    return this.rootRef.current?.contains(element) === true;
  }

  focus = () => {
    if (this.contains(document.activeElement) === false) {
      this.rootRef.current?.focus();
    }
  };

  trapFocus() {
    window.clearTimeout(timer!);
    timer = window.setTimeout(this.focus, 10);
  }

  returnFocus() {
    // When transitioning between pages using hash route state,
    // this anchor is some times lost. Do not attempt to focus
    // on a non-existent anchor.
    if (this.anchor instanceof HTMLElement && typeof this.anchor.focus === 'function') {
      this.anchor.focus();
    }
  }

  componentDidMount() {
    this.trapFocus();

    document.addEventListener('focus', this._onBlur, true);
  }

  componentWillUnmount() {
    document.removeEventListener('focus', this._onBlur, true);

    clearTimeout(timer!);

    this.returnFocus();

    this.anchor = null;
  }

  render() {
    const { children, element, className } = this.props;

    return React.createElement(
      element ?? 'div',
      {
        ref: this.rootRef,
        tabIndex: 0,
        className,
      },
      children
    );
  }
  private _onBlur = (event: FocusEvent) => {
    const current = this.anchor;

    if (current && event.target instanceof Node && current.contains(event.target) === false) {
      event.preventDefault();
      this.trapFocus();
    }
  };
}

export default FocalPoint;
